import Vue from 'vue';
import Vuex from 'vuex';
import {MutationType, SiteId, UserGender} from '@/enums';
import {
	ChapterData,
	DomainData,
	GateAnswer, GateAnswerData,
	GateData,
	PreQuestionList,
	PreQuestionsData,
	ProductInput,
	QuestionData,
	RegistrationPeriod, SpecialEventOption,
	ThemeData, TranslationString
} from '@/types';
import {t} from '@/utils';
import {copy} from 'copy-anything'

import { isVariationDiet } from '@/utils/common';

import {attemptLoadMockData} from '@/utils/mockData';

attemptLoadMockData();

Vue.use(Vuex);

//@ts-ignore
const measureData = window.measurementData || {};

//@ts-ignore
const domains: any = measureData.domains;
//@ts-ignore
const categories: any = measureData.categories;
//@ts-ignore
const items: any = measureData.items;
//@ts-ignore
const gates: any = measureData.gates;
//@ts-ignore
const registrationPeriod: RegistrationPeriod = measureData.registrationPeriod;

const siteId: string = measureData.siteVariation;

const userAge: number | null = measureData?.measureUser?.age;

const urlObject = new URL(window.location.href);
const token: string | 0 = urlObject.searchParams.get('t') || 'test';

const specialEventsOptions: SpecialEventOption[] = measureData.specialEventsOptions;

const preQuestions: PreQuestionsData = measureData.preQuestions;

const preQuestionList: PreQuestionList = new PreQuestionList(preQuestions);

function countThemes(): number {
	let num: number = 0;

	for (const domain of Object.values(domains) as DomainData[]) {
		for (const chapter of Object.values(domain.chapters) as ChapterData[]) {
			// we're only going to have one per chapter now
			num++;
		}
	}

	return num;
}

function formatLocalStoryId(identifierString:string): string {
	let newId: string = `${identifierString}_${token}`;

	return newId;
}

function getLocalStorageItem(identifierString:string): any {
	if (isVariationDiet()) {
		return false;
	}

	return localStorage.getItem(formatLocalStoryId(identifierString));
}

function setLocalStorageItem(identifierString:string, data:string) {
	if (isVariationDiet()) {
		return false;
	}

	localStorage.setItem(formatLocalStoryId(identifierString), data);
}

const gateAnswers: GateAnswer[] = [];
for (const gateData of Object.values(gates) as GateData[]) {
	gateAnswers.push(new GateAnswer(gateData));
}

let store = new Vuex.Store({
	state: {
		userAge: userAge,
		userGender: null as string | null,
		initDomains: domains,
        domains: domains,
        categories: categories,
        items: items,
        gates: gates,
        gateAnswers: gateAnswers,
        gateSurveyFinished: false,
        preQuestionList: preQuestionList,
        preQuestionsFinished: false,
        registrationPeriod: registrationPeriod,
        currentDomain: null,
        currentChapter: null,
        currentTheme: null,
        inputs: [] as ProductInput[],
        finishedThemes: [] as number[],
        noProductQuestions: [] as number[],
        numOfThemes: 0,
		surveyAnswerObject: {} as { [key: string]: boolean },
		currentSiteType: siteId,
		specialEventsOptions: specialEventsOptions,
		specialEventsAnswers: [] as string[],
		sessionDurationStartTime: new Date as Date,
		sessionDurationEndTime: new Date as Date,
		hasSetStartTime: false,
		hasSetEndTime: false,
    },
    getters: {
        allThemesFinished: (state): boolean => {
            return state.finishedThemes.length === state.numOfThemes;
        },
        getInputs: (state) => {
            return state.inputs;
        },
        getNoProductQuestions: (state) => {
            return state.noProductQuestions;
        },
        getSiteType: (state) => {
            return state.currentSiteType;
        },
        getUserAge: (state) => {
            return state.userAge;
        },
        getUserGender: (state) => {
            return state.userGender;
        },
        getSpecialEventsOptions: (state) => {
            return state.specialEventsOptions;
        },
    },
    mutations: {
        [MutationType.INITIALIZE_STORE](state, domain) {
			//initialize numOfThemes
			state.numOfThemes = countThemes();

			// don't set or get things from localstorage if it's the all about diet one
			if (isVariationDiet()) {
				return;
			}

			if (getLocalStorageItem('inputs')) {
				let localStorageArrayString = getLocalStorageItem('inputs') || '[]';
				state.inputs = JSON.parse(localStorageArrayString);
			}

			if (getLocalStorageItem('finishedThemes')) {
				let localStorageArrayString = getLocalStorageItem('finishedThemes') || '[]';
				state.finishedThemes = JSON.parse(localStorageArrayString);
			}

			if (getLocalStorageItem('specialEventsAnswers')) {
				let localStorageArrayString = getLocalStorageItem('specialEventsAnswers') || '[]';
				state.specialEventsAnswers = JSON.parse(localStorageArrayString);
			}

			if (getLocalStorageItem('userAge')) {
				let localStorageString = getLocalStorageItem('userAge') || '0';
				state.userAge = JSON.parse(localStorageString);
			}

			if (getLocalStorageItem('userGender')) {
				let localStorageString = getLocalStorageItem('userGender') || UserGender.GENDER_FEMALE;
				state.userGender = JSON.parse(localStorageString);
			}

			if (getLocalStorageItem('sessionDurationStartTime')) {
				let sessionDurationStartTimeString = getLocalStorageItem('sessionDurationStartTime');
				state.sessionDurationStartTime = JSON.parse(sessionDurationStartTimeString);
			}

			if (getLocalStorageItem('sessionDurationEndTime')) {
				let sessionDurationEndTimeString = getLocalStorageItem('sessionDurationEndTime');
				state.sessionDurationEndTime = JSON.parse(sessionDurationEndTimeString);
			}

			if (getLocalStorageItem('gateSurveyFinished')) {
				let localStorageString = getLocalStorageItem('gateSurveyFinished') || 'false';
				state.gateSurveyFinished = JSON.parse(localStorageString);
			}
			if (getLocalStorageItem('preQuestionsFinished')) {
				let localStorageString = getLocalStorageItem('preQuestionsFinished') || 'false';
				state.preQuestionsFinished = JSON.parse(localStorageString);
			}
			if (getLocalStorageItem('gateAnswers')) {
				let localStorageArrayString = getLocalStorageItem('gateAnswers') || '[]';

				let gateAnswers = JSON.parse(localStorageArrayString) as GateAnswerData[];
				let gateAnswerInstances = gateAnswers.map(obj => new GateAnswer(undefined, obj));
				state.gateAnswers = gateAnswerInstances;
			}
			if (getLocalStorageItem('surveyAnswerObject')) {
				let localStorageObjectString = getLocalStorageItem('surveyAnswerObject') || '{}';
				state.surveyAnswerObject = JSON.parse(localStorageObjectString);

				const preQuestionsQuestionsList = state.preQuestionList.questions;

				for (const question of preQuestionsQuestionsList) {
					const surveyQuestionId: string = question.id;

					if (state.surveyAnswerObject.hasOwnProperty(surveyQuestionId)) {
						const questionAnswer: boolean = state.surveyAnswerObject[surveyQuestionId];
						question.answer = questionAnswer;
					}
				}
			}
        },
        [MutationType.RESET_STORE](state, domain) {
			state.userAge = userAge;
			state.userGender = null;
			state.gateAnswers = gateAnswers;
			state.gateSurveyFinished = false;
			state.preQuestionList = preQuestionList;
			state.preQuestionsFinished = false;
			state.currentDomain = null;
			state.currentChapter = null;
			state.currentTheme = null;
			state.inputs = [];
			state.finishedThemes = [];
			state.noProductQuestions = [];
			state.surveyAnswerObject = {};
			state.specialEventsAnswers = [];
			state.hasSetStartTime = false;
			state.hasSetEndTime = false;
        },
        [MutationType.SET_CURRENT_DOMAIN](state, domain) {
            state.currentDomain = domain;
        },
        [MutationType.SET_CURRENT_CHAPTER](state, chapter) {
            state.currentChapter = chapter;
        },
        [MutationType.SET_CURRENT_THEME](state, theme) {
            state.currentTheme = theme;
        },
        [MutationType.SET_USER_AGE](state, age) {
            state.userAge = age;
			setLocalStorageItem('userAge', JSON.stringify(state.userAge));
        },
        [MutationType.SET_USER_GENDER](state, gender) {
            state.userGender = gender;
			setLocalStorageItem('userGender', JSON.stringify(state.userGender));
        },
        [MutationType.SET_CURRENT_SITE_TYPE](state, siteType) {
            state.currentSiteType = siteType;
        },
        [MutationType.PUSH_INPUT](state, input) {
            state.inputs.push(input);

			setLocalStorageItem('inputs', JSON.stringify(state.inputs));
		},
        [MutationType.REMOVE_INPUT](state, input) {
            const index: number = state.inputs.indexOf(input);
            state.inputs.splice(index, 1);

			setLocalStorageItem('inputs', JSON.stringify(state.inputs));
        },
        [MutationType.SAVE_INPUT_TO_LOCAL](state, input) {
			setLocalStorageItem('inputs', JSON.stringify(state.inputs));
        },
        [MutationType.SET_SESSION_DURATION_START_TIME](state, startTime) {
			if (state.hasSetStartTime) {
				return;
			}

			state.sessionDurationStartTime = startTime;
			setLocalStorageItem('sessionDurationStartTime', JSON.stringify(state.sessionDurationStartTime));

			state.hasSetStartTime = true;
			setLocalStorageItem('hasSetStartTime', JSON.stringify(state.hasSetStartTime));
        },
        [MutationType.SET_SESSION_DURATION_END_TIME](state, endTime) {
			if (state.hasSetEndTime) {
				return;
			}

			state.sessionDurationEndTime = endTime;
			setLocalStorageItem('sessionDurationEndTime', JSON.stringify(state.sessionDurationEndTime));

			state.hasSetEndTime = true;
			setLocalStorageItem('hasSetStartTime', JSON.stringify(state.hasSetEndTime));
        },
        [MutationType.PUSH_FINISHED_THEME](state, theme) {
            if (state.finishedThemes.includes(theme)) {
                return;
            }
            state.finishedThemes.push(theme);

			setLocalStorageItem('finishedThemes', JSON.stringify(state.finishedThemes));
        },
        [MutationType.PUSH_NO_PRODUCT_QUESTION](state, question) {
            if (state.noProductQuestions.includes(question)) {
                return;
            }
            state.noProductQuestions.push(question);
        },
        [MutationType.REMOVE_NO_PRODUCT_QUESTION](state, question) {
            const index: number = state.noProductQuestions.indexOf(question);
            state.noProductQuestions.splice(index, 1);
        },
        [MutationType.FINISH_PRE_QUESTIONS](state) {
            state.preQuestionsFinished = true;
			setLocalStorageItem('preQuestionsFinished', JSON.stringify(state.preQuestionsFinished));
        },
        [MutationType.SET_SPECIAL_EVENT_ANSWERS](state, specialEventsAnswers) {
            state.specialEventsAnswers = specialEventsAnswers;
			setLocalStorageItem('specialEventsAnswers', JSON.stringify(state.specialEventsAnswers));
        },
        [MutationType.SAVE_PRE_QUESTIONS](state) {
			const surveyAnswerObject = {} as { [key: string]: boolean };
			const preQuestionsQuestionsList = state.preQuestionList.questions;

			for (const question of preQuestionsQuestionsList) {
				let id: string = question.id;
				let answer: boolean = question.answer;

				surveyAnswerObject[id] = answer;
			}

			state.surveyAnswerObject = surveyAnswerObject;
			setLocalStorageItem('surveyAnswerObject', JSON.stringify(state.surveyAnswerObject));
        },
        [MutationType.FINISH_GATE_SURVEY](state) {
            state.gateSurveyFinished = true;
			state.domains = copy(state.initDomains);

			for (const domain of Object.values(state.domains) as DomainData[]) {
				const chaptersToFilter: number[] = [];

				for (const chapter of Object.values(domain.chapters) as ChapterData[]) {
					const themesToFilter: number[] = [];

					for (const theme of Object.values(chapter.themes) as ThemeData[]) {
						const questionsToFilter: number[] = [];

						for (const question of Object.values(theme.questions) as QuestionData[]) {
							if (question.gate) {
								const answer: GateAnswer | undefined = state.gateAnswers.find((g) => g.id === question.gate.id);

								if (answer) {
									let selectedDays = answer.selectedDays;

									let minOneSelected = selectedDays.length > 0;
									let minOneDeselected = selectedDays.length < 5;

									const keepQuestion: boolean = (question.gate.value && minOneSelected) || (!question.gate.value && minOneDeselected);
									if (!keepQuestion) {
										questionsToFilter.push(question.id);
									}
								}
							}
						}

						for (const question of questionsToFilter) {
							delete theme.questions[question];
						}

						if (Object.values(theme.questions).length <= 0) {
							themesToFilter.push(theme.id);
						}
					}

					for (const theme of themesToFilter) {
						delete chapter.themes[theme];
					}

					if (Object.values(chapter.themes).length === 0) {
						chaptersToFilter.push(chapter.id);
					}
				}

				for (const chapter of chaptersToFilter) {
					delete domain.chapters[chapter];
				}
			}

			setLocalStorageItem('gateAnswers', JSON.stringify(state.gateAnswers));
			setLocalStorageItem('gateSurveyFinished', JSON.stringify(state.gateSurveyFinished));
		}
    },
    actions: {},
    modules: {}
});


// @ts-ignore
//import * as prefilledInitState from './prefilledInitStateRecommended';
//import * as prefilledInitState from './prefilledInitStateTooManyDrinks';
//import * as prefilledInitState from './prefilledInitStateTooManyEverything';
//import * as prefilledInitState from './prefilledInitStateTooManySolids';
//import * as prefilledInitState from './prefilledInitStateKammerjunker';

if (typeof (prefilledInitState) !== 'undefined') {
	// @ts-ignore
	for (let key in prefilledInitState.default) {
		// @ts-ignore
		store.state[key] = prefilledInitState.default[key];
	}
}

//store.state.userAge = 8;

export default store;
